import { useMutation } from "@tanstack/react-query";
import type {
    LoginRequestType,
    LoginResponseType,
    RegisterRequestType,
    RegisterResponseType,
    VerifyRequestType,
    VerifyResponseType,
} from "@src/types/auth";
import { loginApi, registerApi, verifyApi } from "@src/api/auth";
import { auth } from "@src/api/client";
import type { ErrorResponseType } from "@src/types";
import type { AxiosError } from "axios";

type UseLoginOptions = {
    onSuccess?: (data: LoginResponseType) => void;
    onError?: (err: ErrorResponseType | null) => void;
};

export function useLogin(opts?: UseLoginOptions) {
    return useMutation<LoginResponseType, AxiosError<ErrorResponseType>, LoginRequestType>({
        mutationKey: ["auth", "login"],
        mutationFn: (payload) => loginApi(payload),
        onSuccess: (data) => {
            opts?.onSuccess?.(data);
        },
        onError: (err) => {
            opts?.onError?.(err.response?.data ?? null);
        },
    });
}

type UseRegisterOptions = {
    autoLogin?: boolean;
    onSuccess?: (data: RegisterResponseType) => void;
    onError?: (err: ErrorResponseType | null) => void;
};

export function useRegister(opts?: UseRegisterOptions) {
    return useMutation<RegisterResponseType, AxiosError<ErrorResponseType>, RegisterRequestType>({
        mutationKey: ["auth", "register"],
        mutationFn: (payload) => registerApi(payload),
        onSuccess: async (data, variables) => {
            if (opts?.autoLogin) {
                const username = variables.email;
                try {
                    await auth.login({
                        username,
                        password: variables.password,
                        grant_type: "password",
                    });
                } catch {}
            }
            opts?.onSuccess?.(data);
        },
        onError: (err) => {
            opts?.onError?.(err.response?.data ?? null);
        },
    });
}

type UseVerifyOptions = {
    onSuccess?: (data: VerifyResponseType) => void;
    onError?: (err: ErrorResponseType | null) => void;
};

export function useVerify(opts?: UseVerifyOptions) {
    return useMutation<VerifyResponseType, AxiosError<ErrorResponseType>, VerifyRequestType>({
        mutationKey: ["auth", "register"],
        mutationFn: (payload) => verifyApi(payload),
        onSuccess: (data) => opts?.onSuccess?.(data),
        onError: (err) => opts?.onError?.(err.response?.data ?? null),
    });
}
